import React, { useState, useEffect, useRef } from 'react';
import { sessionApi, SessionListItem } from '../services/api';
import { useSession } from '../contexts/SessionContext';
import './ChatSidebar.css';

interface ChatSidebarProps {
  currentSessionId: string;
  onSessionSelect: (sessionId: string) => void;
  onNewChat: () => void;
}

interface ExtendedSessionListItem extends SessionListItem {
  mode?: 'chat' | 'workflow' | 'research';
  is_pinned?: boolean;
  purge_protected_until?: string;
}

const ChatSidebar: React.FC<ChatSidebarProps> = ({ currentSessionId, onSessionSelect, onNewChat }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [resizing, setResizing] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(320);
  
  // Get sessions from SessionContext
  const { sessions: contextSessions } = useSession();
  const [sessions, setSessions] = useState<ExtendedSessionListItem[]>([]);

  // Sync sessions from context when they change
  useEffect(() => {
    setSessions(contextSessions as ExtendedSessionListItem[]);
  }, [contextSessions]);

  // Load additional session details when sidebar opens
  useEffect(() => {
    if (isOpen) {
      loadSessions();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load user preference from localStorage
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('sidebarOpen');
      if (savedState === 'true') {
        setIsOpen(true);
      }
    } catch {
      // Ignore localStorage errors, use default state
    }
  }, []);

  // Save preference when state changes
  useEffect(() => {
    try {
      localStorage.setItem('sidebarOpen', isOpen.toString());
    } catch {
      // Ignore localStorage errors
    }
  }, [isOpen]);

  // Handle clicks outside sidebar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && 
          sidebarRef.current && 
          !sidebarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Cmd/Ctrl + / to toggle sidebar
      if ((event.metaKey || event.ctrlKey) && event.key === '/') {
        event.preventDefault();
        setIsOpen(prev => !prev);
      }
      // ESC to close sidebar
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await sessionApi.list();
      // Merge with context sessions to get latest titles
      const apiSessions = response.data.sessions as ExtendedSessionListItem[];
      const mergedSessions = apiSessions.map(apiSession => {
        const contextSession = contextSessions.find(cs => cs.session_id === apiSession.session_id);
        if (contextSession) {
          // Use title from context if available (it's the most up-to-date)
          return { ...apiSession, title: contextSession.title };
        }
        return apiSession;
      });
      setSessions(mergedSessions);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionClick = (sessionId: string) => {
    onSessionSelect(sessionId);
    setIsOpen(false);  // Always close sidebar after selecting a session
  };

  const filteredSessions = sessions.filter(session => {
    // Always show current session
    if (session.session_id === currentSessionId) return true;
    
    // Hide empty sessions unless they're pinned
    if (session.message_count === 0 && session.file_count === 0 && !session.is_pinned) {
      return false;
    }
    
    // Apply search filter
    return session.title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Group sessions by date
  const groupSessionsByDate = (sessions: ExtendedSessionListItem[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups: { [key: string]: SessionListItem[] } = {
      'Today': [],
      'Yesterday': [],
      'This Week': [],
      'Older': []
    };

    sessions.forEach(session => {
      const sessionDate = new Date(session.last_activity);
      if (sessionDate >= today) {
        groups['Today'].push(session);
      } else if (sessionDate >= yesterday) {
        groups['Yesterday'].push(session);
      } else if (sessionDate >= weekAgo) {
        groups['This Week'].push(session);
      } else {
        groups['Older'].push(session);
      }
    });

    // Remove empty groups
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });

    return groups;
  };

  const groupedSessions = groupSessionsByDate(filteredSessions);

  return (
    <>
      {/* Peek strip */}
      <div 
        className={`sidebar-peek ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        title="Click to open chat history (Cmd/Ctrl + /)"
      >
        <div className="peek-icon">
          →
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && <div className="sidebar-backdrop" onClick={() => setIsOpen(false)} />}

      {/* Sidebar */}
      <div 
        ref={sidebarRef} 
        className={`chat-sidebar ${isOpen ? 'open' : ''}`}
        style={{ width: isOpen ? `${sidebarWidth}px` : undefined }}
      >
        {/* Resize handle */}
        {isOpen && (
          <div
            className="sidebar-resize-handle"
            onMouseDown={(e) => {
              e.preventDefault();
              setResizing(true);
              const startX = e.clientX;
              const startWidth = sidebarWidth;
              
              const handleMouseMove = (e: MouseEvent) => {
                const newWidth = Math.min(600, Math.max(280, startWidth + (e.clientX - startX)));
                setSidebarWidth(newWidth);
              };
              
              const handleMouseUp = () => {
                setResizing(false);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };
              
              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
          />
        )}
        
        <div className="sidebar-header">
          <button className="btn btn-primary btn-sm new-chat-button" onClick={() => {
            onNewChat();
            setIsOpen(false);  // Close sidebar after creating new chat
          }}>
            + New Chat
          </button>
          <button 
            className="btn btn-ghost btn-icon btn-sm info-button" 
            onClick={() => setShowInfoModal(true)}
            title="Session Management Info"
          >
            ℹ️
          </button>
          <button className="btn btn-ghost btn-icon btn-sm sidebar-close" onClick={() => setIsOpen(false)}>
            ×
          </button>
        </div>

        <div className="sidebar-search">
          <input
            type="text"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="sessions-list">
          {loading ? (
            <div className="loading-state">Loading chats...</div>
          ) : Object.keys(groupedSessions).length === 0 ? (
            <div className="empty-state">
              {searchQuery ? 'No chats found' : 'No chat history yet'}
            </div>
          ) : (
            Object.entries(groupedSessions).map(([groupName, groupSessions]) => (
              <div key={groupName} className="session-group">
                <div className="group-header">{groupName}</div>
                {groupSessions.map(session => (
                  <ChatListItem
                    key={session.session_id}
                    session={session}
                    isActive={session.session_id === currentSessionId}
                    onClick={() => handleSessionClick(session.session_id)}
                    onDelete={(sessionId) => {
                      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
                      if (sessionId === currentSessionId) {
                        onNewChat();
                      }
                    }}
                    onPin={(sessionId, pinned) => {
                      setSessions(prev => prev.map(s => 
                        s.session_id === sessionId ? { ...s, is_pinned: pinned } : s
                      ));
                    }}
                    onRename={(sessionId, newTitle) => {
                      setSessions(prev => prev.map(s => 
                        s.session_id === sessionId ? { ...s, title: newTitle } : s
                      ));
                    }}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Purge Info Modal */}
      {showInfoModal && (
        <PurgeInfoModal onClose={() => setShowInfoModal(false)} />
      )}
    </>
  );
};

interface ChatListItemProps {
  session: ExtendedSessionListItem;
  isActive: boolean;
  onClick: () => void;
  onDelete?: (sessionId: string) => void;
  onPin?: (sessionId: string, pinned: boolean) => void;
  onRename?: (sessionId: string, newTitle: string) => void;
}

const ChatListItem: React.FC<ChatListItemProps> = ({ session, isActive, onClick, onDelete, onPin, onRename }) => {
  const { generateTitle } = useSession();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showGenerateBtn, setShowGenerateBtn] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState(session.title);
  
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleGenerateTitle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering session selection
    setIsGenerating(true);
    try {
      await generateTitle(session.session_id);
      // Title will be updated through context
    } catch (error) {
      console.error('Failed to generate title:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Check if this session needs a generate title button
  const needsTitleGeneration = session.title === "New Chat" && session.message_count >= 2;
  
  // Calculate age for visual styling
  const getAgeClass = () => {
    const date = new Date(session.last_activity);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffDays === 0) return 'age-today';
    if (diffDays === 1) return 'age-yesterday';
    if (diffDays <= 7) return 'age-week';
    if (diffDays <= 30) return 'age-month';
    return 'age-old';
  };
  
  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Delete "${session.title}"?`)) {
      try {
        await sessionApi.delete(session.session_id);
        if (onDelete) onDelete(session.session_id);
      } catch (error) {
        console.error('Failed to delete session:', error);
        alert('Failed to delete session');
      }
    }
  };
  
  const handlePin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await sessionApi.pin(session.session_id, !session.is_pinned);
      if (onPin) onPin(session.session_id, !session.is_pinned);
    } catch (error) {
      console.error('Failed to pin session:', error);
    }
  };
  
  const handleRename = async () => {
    if (newTitle && newTitle !== session.title) {
      try {
        await sessionApi.rename(session.session_id, newTitle);
        if (onRename) onRename(session.session_id, newTitle);
      } catch (error) {
        console.error('Failed to rename session:', error);
        setNewTitle(session.title);
      }
    }
    setIsRenaming(false);
  };
  
  const getModeIcon = () => {
    switch (session.mode) {
      case 'workflow': return '🔧';
      case 'research': return '🔍';
      case 'chat': return '💬';
      default: return '💬';
    }
  };

  return (
    <div 
      className={`chat-list-item ${isActive ? 'active' : ''} ${getAgeClass()}`}
      onClick={onClick}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="chat-title-row">
        <span className="mode-icon" title={session.mode}>{getModeIcon()}</span>
        {isRenaming ? (
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
              if (e.key === 'Escape') {
                setNewTitle(session.title);
                setIsRenaming(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            className="rename-input"
            autoFocus
          />
        ) : (
          <div className="chat-title">
            {session.is_pinned && <span className="pin-indicator">📌</span>}
            {session.title}
          </div>
        )}
        {showActions && (
          <div className="chat-actions">
            {needsTitleGeneration && (
              <button
                className="action-btn"
                onClick={handleGenerateTitle}
                disabled={isGenerating}
                title="Generate title"
              >
                {isGenerating ? '...' : '✨'}
              </button>
            )}
            <button
              className="action-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsRenaming(true);
              }}
              title="Rename"
            >
              ✏️
            </button>
            <button
              className="action-btn"
              onClick={handlePin}
              title={session.is_pinned ? 'Unpin' : 'Pin'}
            >
              {session.is_pinned ? '📌' : '📍'}
            </button>
            <button
              className="action-btn delete-btn"
              onClick={handleDelete}
              title="Delete"
            >
              🗑️
            </button>
          </div>
        )}
      </div>
      <div className="chat-meta">
        <span className="chat-time">{formatTime(session.last_activity)}</span>
        <span className="chat-stats">
          {session.message_count === 0 && session.file_count > 0 
            ? `${session.file_count} file${session.file_count > 1 ? 's' : ''} ready`
            : session.message_count === 1 
              ? `1 message${session.file_count > 0 ? ` • ${session.file_count} file${session.file_count > 1 ? 's' : ''}` : ''}`
              : `${session.message_count} messages${session.file_count > 0 ? ` • ${session.file_count} file${session.file_count > 1 ? 's' : ''}` : ''}`
          }
        </span>
      </div>
    </div>
  );
};

// Purge Info Modal Component
const PurgeInfoModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content purge-info-modal">
        <div className="modal-header">
          <h2>Session Management</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="info-section">
            <h3>🗑️ Auto-Purge</h3>
            <p>Sessions are automatically deleted after 30 days of inactivity to keep your workspace clean.</p>
            <ul>
              <li>All associated files and data are permanently removed</li>
              <li>You'll receive a warning 3 days before deletion</li>
              <li>Current session is always protected</li>
            </ul>
          </div>
          
          <div className="info-section">
            <h3>📌 Pin Sessions</h3>
            <p>Pin important sessions to protect them from auto-purge.</p>
            <ul>
              <li>Pinned sessions are never automatically deleted</li>
              <li>Click the pin icon to toggle protection</li>
              <li>Pinned sessions appear with a 📌 indicator</li>
            </ul>
          </div>
          
          <div className="info-section">
            <h3>💾 Saved Workflows</h3>
            <p>Sessions with saved workflows are protected from deletion.</p>
            <ul>
              <li>Published workflows prevent session deletion</li>
              <li>Draft workflows alone don't protect sessions</li>
              <li>Archive workflows before deleting their sessions</li>
            </ul>
          </div>
          
          <div className="info-section">
            <h3>🎨 Visual Indicators</h3>
            <p>Sessions are color-coded by age:</p>
            <ul>
              <li><span className="age-indicator age-today">●</span> Today - Fresh and active</li>
              <li><span className="age-indicator age-yesterday">●</span> Yesterday - Recent activity</li>
              <li><span className="age-indicator age-week">●</span> This week - Still recent</li>
              <li><span className="age-indicator age-month">●</span> This month - Getting older</li>
              <li><span className="age-indicator age-old">●</span> Older - May be purged soon</li>
            </ul>
          </div>
          
          <div className="info-section">
            <h3>🔧 Session Actions</h3>
            <p>Hover over any session to reveal management options:</p>
            <ul>
              <li>✏️ Rename - Give sessions meaningful names</li>
              <li>📌 Pin/Unpin - Toggle purge protection</li>
              <li>🗑️ Delete - Manually remove a session</li>
              <li>✨ Generate title - Auto-generate from conversation</li>
            </ul>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </>
  );
};

export default ChatSidebar;